import { atom, useAtomValue } from "jotai";
import { useMemo } from "react";

import { schema } from "@moment/prosemirror-schema";

import { $editorState } from "../../internal/prosemirror/editor-state.atom";
import { editorStateKeyForDocsRoute } from "../../internal/prosemirror/editorStateKey";

/**
 * Derived atom that returns a stable boolean — only triggers re-renders when
 * the managed-section presence actually changes, not on every editor transaction.
 */
const $hasManagedSection = (key: string) =>
	atom((get) => {
		const editorState = get($editorState(key));
		if (!editorState) {
			return false;
		}
		const doc = editorState.doc;
		for (let i = 0; i < doc.childCount; i++) {
			const child = doc.child(i);
			if (
				child.type === schema.nodes.readonly_section &&
				child.attrs["managedSection"] === true
			) {
				return true;
			}
		}
		return false;
	});

/**
 * Returns whether the given page's ProseMirror doc contains a readonly_section
 * node with managedSection=true — i.e. a Moment-managed agent instruction
 * section wrapped in <section readonly data-variant="moment-agents-template">.
 *
 * Uses a derived atom so that the component only re-renders when the boolean
 * value actually changes, not on every editor transaction.
 */
export const useHasManagedSection = (
	documentId: string | undefined,
	pageId: string | undefined
): boolean => {
	const editorKey = useMemo(
		() => (documentId && pageId ? editorStateKeyForDocsRoute({ documentId, pageId }) : ""),
		[documentId, pageId]
	);

	const hasManagedAtom = useMemo(() => $hasManagedSection(editorKey), [editorKey]);

	return useAtomValue(hasManagedAtom);
};
